import React from 'react';
import { CerfaState, VehicleData, PersonData } from '../types';

interface CerfaFormProps {
  data: CerfaState;
  onChange?: (newData: CerfaState) => void;
  readOnly?: boolean;
  isPdfMode?: boolean; 
}

// Helper to remove AI artifacts or "NONE" strings
const cleanValue = (val: string | undefined | null) => {
  if (!val) return "";
  const v = String(val).trim();
  // Filter out common AI hallucinations for empty fields
  if (["NONE", "NULL", "UNDEFINED", "NAN", "N/A"].includes(v.toUpperCase())) return "";
  return v;
};

// Helper to render Input or Text-Div based on mode
const FormInput = ({ 
  value, 
  onChange, 
  className, 
  readOnly, 
  isPdfMode, 
  type = "text" 
}: { 
  value: string; 
  onChange?: (val: string) => void; 
  className: string; 
  readOnly?: boolean; 
  isPdfMode?: boolean;
  type?: string;
}) => {
  if (isPdfMode) {
    // PDF MODE: Render as a static div with explicit flex centering
    const displayValue = cleanValue(value);
    
    return (
      <div
        className={`${className} !text-black`}
        style={{
          display: "flex",
          alignItems: "center",
          minHeight: "16px",
          lineHeight: "1.1",
          fontFamily: "Arial, Helvetica, sans-serif",
          fontSize: "11px",
          fontWeight: "bold",
          whiteSpace: "nowrap",
          borderBottom: "1px solid #999",
          backgroundColor: displayValue ? "#f7f7f7" : "transparent",
          paddingTop: "0px",
          paddingBottom: "2px",
          paddingLeft: "2px",
          paddingRight: "2px",
        }}
      >
        {displayValue}
      </div>
    );
  }

  // INTERACTIVE MODE
  return (
    <input 
      type={type}
      className={className}
      value={value}
      onChange={(e) => onChange && onChange(e.target.value)}
      readOnly={readOnly}
    />
  );
};

const SingleCerfaPage: React.FC<{ 
  data: CerfaState; 
  onChange?: (newData: CerfaState) => void; 
  copyNumber: 1 | 2;
  readOnly?: boolean;
  isPdfMode?: boolean;
}> = ({ data, onChange, copyNumber, readOnly = false, isPdfMode = false }) => {
  
  const updateVehicle = (field: keyof VehicleData, value: string) => {
    if (onChange) onChange({ ...data, vehicle: { ...data.vehicle, [field]: value } });
  };

  const updatePerson = (type: 'seller' | 'buyer', field: keyof PersonData, value: any) => {
    if (onChange) onChange({ ...data, [type]: { ...data[type] as PersonData, [field]: value } });
  };

  const Checkbox = ({ checked, onClick, label, className = "" }: { checked: boolean; onClick?: () => void; label?: string, className?: string }) => (
    <div className={`flex items-center cursor-pointer ${className}`} onClick={!readOnly && !isPdfMode ? onClick : undefined}>
      {/* Box is always white with black border */}
      <div className={`w-3.5 h-3.5 border border-black flex items-center justify-center mr-1.5 flex-shrink-0 bg-white`}>
        {checked && (
          // SVG X path, fill BLACK for standard look
          <svg viewBox="0 0 24 24" className="w-full h-full p-[1px] pointer-events-none fill-black">
            <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
          </svg>
        )}
      </div>
      {label && <span className="text-[9px] font-medium text-black leading-tight" style={isPdfMode ? {fontFamily: 'Arial, sans-serif'} : {}}>{label}</span>}
    </div>
  );

  // A4 size in pixels at 96 DPI: 794px x 1123px
  const pageStyle = isPdfMode 
    ? { width: '794px', height: '1123px', padding: '18px 24px', backgroundColor: 'white' } 
    : { width: '210mm', height: '297mm', padding: '6mm 8mm' };

  // Conditional classes for background colors (avoid opacity in PDF mode)
  const headerBg = isPdfMode ? 'bg-transparent' : 'bg-blue-50/30';
  const sectionBorder = 'border border-blue-600';

  return (
    <div className="bg-white text-black font-sans text-xs relative overflow-hidden mx-auto shadow-sm box-border flex flex-col" style={pageStyle}>
      
      {/* Header */}
      <div className="flex justify-between items-start mb-0.5 border-b border-blue-900 pb-0 shrink-0">
        <div className="w-1/4">
           <div className="border border-black p-1 inline-block text-[7px] text-center font-serif leading-none">
              Liberté • Égalité • Fraternité<br/>
              RÉPUBLIQUE FRANÇAISE<br/>
              <span className="font-bold block mt-0.5 text-[8px]">MINISTÈRE<br/>DE L'INTÉRIEUR</span>
           </div>
        </div>
        <div className="w-1/2 text-center">
           <h1 className="text-base font-black text-blue-800 uppercase leading-none" style={isPdfMode ? {color: '#1e40af'} : {}}>Certificat de Cession<br/>D'un Véhicule d'Occasion</h1>
           <p className="text-[9px] mt-0.5 font-bold leading-tight">(à remplir par l'ancien propriétaire et le nouveau propriétaire)</p>
           <p className="text-[8px] italic mt-0.5">Articles R322-4 et R322-9 du code de la route</p>
        </div>
        <div className="w-1/4 text-right">
             <div className="inline-block text-center border-l pl-3 border-gray-300">
                <div className="font-bold text-xl font-serif italic leading-none text-gray-800">cerfa</div>
                <div className="font-bold text-xs">N° 15776*01</div>
             </div>
        </div>
      </div>

      {/* LE VEHICULE */}
      <div className={`${sectionBorder} mb-1 relative pt-7 px-2 pb-2 rounded-sm shrink-0`}>
        <h2
          className="absolute left-3 px-2 text-blue-700 font-bold uppercase text-xs bg-white"
          style={isPdfMode ? { color: "#1d4ed8", top: "2px" } : { top: "2px" }}
        >
          Le Véhicule <span className="text-[9px] text-gray-600 normal-case font-normal ml-1" style={isPdfMode ? {color: '#4b5563'} : {}}>(à remplir par l'ancien propriétaire)</span>
          <span className="float-right text-[9px] italic text-gray-500 font-medium ml-2">
            Exemplaire {copyNumber} destiné {copyNumber === 1 ? "à l'ancien propriétaire" : "au nouveau propriétaire"}
          </span>
        </h2>

        <div className="border border-gray-300/80 rounded-sm p-2 bg-white">
          <div className="flex gap-4 mb-2">
            <div className="w-1/2">
              <label className="block text-[8px] font-bold mb-0.5">(A) Numéro d'immatriculation du véhicule</label>
              <FormInput
                className={`w-full border-b border-black ${headerBg} font-mono uppercase tracking-widest h-5 box-border`}
                value={data.vehicle.registrationNumber}
                onChange={(v) => updateVehicle("registrationNumber", v)}
                readOnly={readOnly}
                isPdfMode={isPdfMode}
              />
            </div>
            <div className="w-1/2">
              <label className="block text-[8px] font-bold mb-0.5">(E) Numéro d'identification du véhicule (VIN)</label>
              <FormInput
                className={`w-full border-b border-black ${headerBg} font-mono uppercase tracking-wide h-5 box-border`}
                value={data.vehicle.vin}
                onChange={(v) => updateVehicle("vin", v)}
                readOnly={readOnly}
                isPdfMode={isPdfMode}
              />
            </div>
          </div>

          <div className="flex gap-2 mb-2">
            <div className="w-1/4">
              <label className="block text-[8px] font-bold mb-0.5">(B) Date 1ère immat.</label>
              <FormInput
                className={`w-full border-b border-black ${headerBg} font-medium h-4 box-border`}
                value={data.vehicle.firstRegistrationDate}
                onChange={(v) => updateVehicle("firstRegistrationDate", v)}
                readOnly={readOnly}
                isPdfMode={isPdfMode}
              />
            </div>
            <div className="w-1/4">
              <label className="block text-[8px] font-bold mb-0.5">(D.1) Marque</label>
              <FormInput
                className={`w-full border-b border-black ${headerBg} uppercase font-medium h-4 box-border`}
                value={data.vehicle.brand}
                onChange={(v) => updateVehicle("brand", v)}
                readOnly={readOnly}
                isPdfMode={isPdfMode}
              />
            </div>
            <div className="w-1/4">
              <label className="block text-[8px] font-bold mb-0.5">(D.2) Type / Variante / Version</label>
              <FormInput
                className={`w-full border-b border-black ${headerBg} uppercase font-medium h-4 box-border`}
                value={data.vehicle.type}
                onChange={(v) => updateVehicle("type", v)}
                readOnly={readOnly}
                isPdfMode={isPdfMode}
              />
            </div>
            <div className="w-1/4">
              <label className="block text-[8px] font-bold mb-0.5">(D.3) Désignation commerciale</label>
              <FormInput
                className={`w-full border-b border-black ${headerBg} uppercase font-medium h-4 box-border`}
                value={data.vehicle.model}
                onChange={(v) => updateVehicle("model", v)}
                readOnly={readOnly}
                isPdfMode={isPdfMode}
              />
            </div>
            <div className="w-1/4">
              <label className="block text-[8px] font-bold mb-0.5">(J.1) Genre national</label>
              <FormInput
                className={`w-full border-b border-black ${headerBg} uppercase font-medium h-4 box-border`}
                value={data.vehicle.genre}
                onChange={(v) => updateVehicle("genre", v)}
                readOnly={readOnly}
                isPdfMode={isPdfMode}
              />
            </div>
          </div>

          <div className="flex gap-4 items-end mb-2">
            <div className="w-1/2 flex items-center">
              <label className="text-[8px] font-bold mr-2 whitespace-nowrap">Kilométrage inscrit au compteur :</label>
              <FormInput
                className={`flex-1 border-b border-black ${headerBg} font-mono text-right h-4 box-border`}
                value={data.vehicle.mileage}
                onChange={(v) => updateVehicle("mileage", v)}
                readOnly={readOnly}
                isPdfMode={isPdfMode}
              />
              <span className="ml-1 text-[9px]">km</span>
            </div>
            <div className="w-1/2 flex items-center gap-2">
              <span className="text-[8px] font-bold whitespace-nowrap">Certificat d'immatriculation présent ?</span>
              <div className="flex gap-3">
                <Checkbox
                  label="OUI"
                  checked={data.vehicle.ciPresent !== false}
                  onClick={() => onChange && onChange({ ...data, vehicle: { ...data.vehicle, ciPresent: true } })}
                />
                <Checkbox
                  label="NON"
                  checked={data.vehicle.ciPresent === false}
                  onClick={() => onChange && onChange({ ...data, vehicle: { ...data.vehicle, ciPresent: false } })}
                />
              </div>
            </div>
          </div>

          {data.vehicle.ciPresent !== false ? (
            <div className="flex gap-4 mb-2">
              <div className="w-1/2">
                <label className="block text-[8px] font-bold mb-0.5">N° de formule</label>
                <FormInput
                  className={`w-full border-b border-black ${headerBg} font-mono tracking-wide h-4 box-border`}
                  value={data.vehicle.ciFormulaNumber || ""}
                  onChange={(v) => updateVehicle("ciFormulaNumber", v)}
                  readOnly={readOnly}
                  isPdfMode={isPdfMode}
                />
                <p className="text-[7px] text-gray-500 mt-0.5">Figure sur le 1er volet du CI (format AB-123-CD).</p>
              </div>
              <div className="w-1/2">
                <label className="block text-[8px] font-bold mb-0.5">(I) Date du certificat (ancien format)</label>
                <FormInput
                  type="date"
                  className={`w-full border-b border-black ${headerBg} h-4 box-border`}
                  value={data.vehicle.ciIssueDate || ""}
                  onChange={(v) => updateVehicle("ciIssueDate", v)}
                  readOnly={readOnly}
                  isPdfMode={isPdfMode}
                />
              </div>
            </div>
          ) : (
            <div className="mb-2">
              <label className="block text-[8px] font-bold mb-0.5">Motif d'absence de certificat d'immatriculation</label>
              <FormInput
                className={`w-full border-b border-black ${headerBg} h-4 box-border`}
                value={data.vehicle.ciAbsenceReason || ""}
                onChange={(v) => updateVehicle("ciAbsenceReason", v)}
                readOnly={readOnly}
                isPdfMode={isPdfMode}
              />
            </div>
          )}
        </div>
      </div>

      {/* ANCIEN PROPRIETAIRE */}
      <div className={`${sectionBorder} mb-1 relative pt-7 px-2 pb-2 rounded-sm shrink-0`}>
         <h2
           className="absolute left-3 px-2 text-blue-700 font-bold uppercase text-xs bg-white"
           style={isPdfMode ? { color: "#1d4ed8", top: "2px" } : { top: "2px" }}
         >
           Ancien Propriétaire
         </h2>
         
         <div className="flex mb-2">
            <Checkbox label="Personne Physique" checked={!data.seller.isCompany} onClick={() => updatePerson('seller', 'isCompany', false)} className="mr-4" />
            <Checkbox label="Personne Morale" checked={data.seller.isCompany} onClick={() => updatePerson('seller', 'isCompany', true)} />
         </div>

        <div className="flex gap-2 mb-2">
            <div className="w-2/3">
                 <label className="block text-[8px] font-bold mb-0.5">Nom / Nom d'usage / Raison Sociale</label>
                 <FormInput 
                   className={`w-full border-b border-black ${headerBg} uppercase font-bold h-4 box-border`} 
                   value={data.seller.lastName} 
                   onChange={v => updatePerson('seller', 'lastName', v)} 
                   readOnly={readOnly} isPdfMode={isPdfMode}
                 />
            </div>
            <div className="w-1/3 flex gap-2">
              {!data.seller.isCompany && (
                <div className="flex-1">
                  <label className="block text-[8px] font-bold mb-0.5">Prénom</label>
                  <FormInput 
                    className={`w-full border-b border-black ${headerBg} uppercase h-4 box-border`} 
                    value={data.seller.firstName} 
                    onChange={v => updatePerson('seller', 'firstName', v)} 
                    readOnly={readOnly} isPdfMode={isPdfMode}
                  />
                </div>
              )}
              {data.seller.isCompany && (
                <div className="flex-1">
                  <label className="block text-[8px] font-bold mb-0.5">N° SIRET</label>
                  <FormInput 
                    className={`w-full border-b border-black ${headerBg} uppercase h-4 box-border`} 
                    value={data.seller.siret} 
                    onChange={v => updatePerson('seller', 'siret', v)} 
                    readOnly={readOnly} isPdfMode={isPdfMode}
                  />
                </div>
              )}
              <div className="w-12">
                <label className="block text-[8px] font-bold mb-0.5">Sexe</label>
                <FormInput 
                  className={`w-full border-b border-black ${headerBg} uppercase h-4 box-border`} 
                 value={data.seller.gender || ''} 
                 onChange={v => updatePerson('seller', 'gender', v)} 
                 readOnly={readOnly} isPdfMode={isPdfMode}
                />
              </div>
            </div>
       </div>

         <div className="mb-2">
             <label className="block text-[8px] font-bold mb-0.5">Adresse Complète (N°, Voie, Complément)</label>
             <FormInput 
               className={`w-full border-b border-black ${headerBg} h-4 box-border`} 
               value={data.seller.address} 
               onChange={v => updatePerson('seller', 'address', v)} 
               readOnly={readOnly} isPdfMode={isPdfMode}
             />
         </div>

         <div className="flex gap-2 mb-2">
             <div className="w-1/4">
                <label className="block text-[8px] font-bold mb-0.5">Code Postal</label>
                <FormInput 
                  className={`w-full border-b border-black ${headerBg} font-mono tracking-widest h-4 box-border`} 
                  value={data.seller.postalCode} 
                  onChange={v => updatePerson('seller', 'postalCode', v)} 
                  readOnly={readOnly} isPdfMode={isPdfMode}
                />
             </div>
             <div className="w-3/4">
                <label className="block text-[8px] font-bold mb-0.5">Commune</label>
                <FormInput 
                  className={`w-full border-b border-black ${headerBg} uppercase h-4 box-border`} 
                  value={data.seller.city} 
                  onChange={v => updatePerson('seller', 'city', v)} 
                  readOnly={readOnly} isPdfMode={isPdfMode}
                />
             </div>
         </div>

            <div className="bg-gray-50/50 p-2 border border-gray-200 mb-2">
            <p className="font-bold text-[9px] mb-1">Je certifie (cocher la case correspondante) :</p>
            <div className="flex gap-8 mb-2">
                 <Checkbox label="Céder" checked={!data.isCessionForDestruction} onClick={() => onChange && onChange({...data, isCessionForDestruction: false})} />
                 <Checkbox label="Céder pour destruction" checked={data.isCessionForDestruction} onClick={() => onChange && onChange({...data, isCessionForDestruction: true})} />
            </div>
            
             <p className="font-bold text-[9px] mb-1">Je certifie en outre :</p>
             <div className="space-y-1 mb-2">
                 <Checkbox label="Avoir remis au nouveau propriétaire un certificat établi depuis moins de 15 jours..." checked={data.agreedToConditions} onClick={() => onChange && onChange({...data, agreedToConditions: !data.agreedToConditions})} className="items-start" />
                 <Checkbox label="Que ce véhicule n'a pas subi de transformation notable..." checked={data.agreedToConditions} onClick={() => onChange && onChange({...data, agreedToConditions: !data.agreedToConditions})} className="items-start" />
             </div>
             {data.isCessionForDestruction && (
               <div className="mt-2">
                 <label className="block text-[8px] font-bold mb-0.5">N° d'agrément VHU (si destruction)</label>
                 <FormInput 
                   className={`w-full border-b border-black ${headerBg} h-4 box-border`} 
                   value={data.vhuApprovalNumber || ''} 
                   onChange={v => onChange && onChange({...data, vhuApprovalNumber: v})} 
                   readOnly={readOnly} isPdfMode={isPdfMode}
                 />
               </div>
             )}
         </div>

         <div className="flex justify-between items-end pr-2">
             <div className="flex items-end gap-1 mb-3 w-1/2">
                <span className="font-bold text-[10px] whitespace-nowrap">Fait à</span>
                <FormInput 
                  className="border-b border-black bg-transparent px-1 font-bold h-4 box-border flex-1 uppercase" 
                  value={data.seller.city} 
                  onChange={v => updatePerson('seller', 'city', v)} 
                  readOnly={readOnly} isPdfMode={isPdfMode}
                />
                <span className="font-bold text-[10px] whitespace-nowrap ml-1">, le</span>
                <FormInput 
                  type="date"
                  className="border-b border-black bg-transparent px-1 font-bold h-4 box-border flex-1" 
                  value={data.saleDate} 
                  onChange={v => onChange && onChange({...data, saleDate: v})} 
                  readOnly={readOnly} isPdfMode={isPdfMode}
                />
             </div>
             
             <div className="text-center w-52">
                 <p className="font-bold text-[9px] mb-0.5">Signature de l'ancien propriétaire,</p>
                 <p className="italic text-[7px] mb-1 leading-tight text-gray-600">(Pour les sociétés : nom et qualité<br/>du signataire et cachet)</p>
                 <div className="h-10 border border-gray-300 bg-white"></div>
             </div>
         </div>
      </div>

      {/* NOUVEAU PROPRIETAIRE */}
      <div className={`${sectionBorder} relative pt-7 px-2 pb-2 rounded-sm flex-grow flex flex-col justify-between shrink-0`}>
         <h2
           className="absolute left-3 px-2 text-blue-700 font-bold uppercase text-xs bg-white"
           style={isPdfMode ? { color: "#1d4ed8", top: "2px" } : { top: "2px" }}
         >
           Nouveau Propriétaire
         </h2>
         
         <div>
            <div className="flex mb-1 gap-4 flex-wrap">
                <Checkbox label="Personne Physique" checked={!data.buyer.isCompany} onClick={() => updatePerson('buyer', 'isCompany', false)} />
                <Checkbox label="Personne Morale" checked={data.buyer.isCompany} onClick={() => updatePerson('buyer', 'isCompany', true)} />
            </div>

            <div className="flex gap-2 mb-1">
                <div className="w-2/3">
                    <label className="block text-[8px] font-bold mb-0.5">Nom / Nom d'usage / Raison Sociale</label>
                    <FormInput 
                      className={`w-full border-b border-black ${headerBg} uppercase font-bold h-4 box-border`} 
                      value={data.buyer.lastName} 
                      onChange={v => updatePerson('buyer', 'lastName', v)} 
                      readOnly={readOnly} isPdfMode={isPdfMode}
                    />
                </div>
                <div className="w-1/3 flex gap-2">
                    <div className="flex-1">
                        <label className="block text-[8px] font-bold mb-0.5">Prénom</label>
                        <FormInput 
                          className={`w-full border-b border-black ${headerBg} uppercase h-4 box-border`} 
                          value={data.buyer.firstName} 
                          onChange={v => updatePerson('buyer', 'firstName', v)} 
                          readOnly={readOnly} isPdfMode={isPdfMode}
                        />
                    </div>
                    <div className="w-12">
                        <label className="block text-[8px] font-bold mb-0.5">Sexe</label>
                        <FormInput 
                          className={`w-full border-b border-black ${headerBg} uppercase h-4 box-border`} 
                          value={data.buyer.gender || ''} 
                          onChange={v => updatePerson('buyer', 'gender', v)} 
                          readOnly={readOnly} isPdfMode={isPdfMode}
                        />
                    </div>
                </div>
            </div>
            {data.buyer.isCompany && (
              <div className="mb-1">
                <label className="block text-[8px] font-bold mb-0.5">N° SIRET (nouveau propriétaire)</label>
                <FormInput 
                  className={`w-full border-b border-black ${headerBg} uppercase h-4 box-border`} 
                  value={data.buyer.siret} 
                  onChange={v => updatePerson('buyer', 'siret', v)} 
                  readOnly={readOnly} isPdfMode={isPdfMode}
                />
              </div>
            )}

            <div className="mb-1">
                <label className="block text-[8px] font-bold mb-0.5">Adresse Complète</label>
                <FormInput 
                  className={`w-full border-b border-black ${headerBg} h-4 box-border`} 
                  value={data.buyer.address} 
                  onChange={v => updatePerson('buyer', 'address', v)} 
                  readOnly={readOnly} isPdfMode={isPdfMode}
                />
            </div>

            <div className="flex gap-2 mb-1">
                <div className="w-1/4">
                    <label className="block text-[8px] font-bold mb-0.5">Code Postal</label>
                    <FormInput 
                      className={`w-full border-b border-black ${headerBg} font-mono tracking-widest h-4 box-border`} 
                      value={data.buyer.postalCode} 
                      onChange={v => updatePerson('buyer', 'postalCode', v)} 
                      readOnly={readOnly} isPdfMode={isPdfMode}
                    />
                </div>
                <div className="w-3/4">
                    <label className="block text-[8px] font-bold mb-0.5">Commune</label>
                    <FormInput 
                      className={`w-full border-b border-black ${headerBg} uppercase h-4 box-border`} 
                      value={data.buyer.city} 
                      onChange={v => updatePerson('buyer', 'city', v)} 
                    readOnly={readOnly} isPdfMode={isPdfMode}
                  />
                </div>
            </div>

            {!data.buyer.isCompany && (
              <div className="flex gap-2 mb-1">
                <div className="w-1/2">
                  <label className="block text-[8px] font-bold mb-0.5">Né(e) le</label>
                  <FormInput 
                    type="date"
                    className={`w-full border-b border-black ${headerBg} h-4 box-border`} 
                    value={data.buyer.birthDate || ''} 
                    onChange={v => updatePerson('buyer', 'birthDate', v)} 
                    readOnly={readOnly} isPdfMode={isPdfMode}
                  />
                </div>
                <div className="w-1/2">
                  <label className="block text-[8px] font-bold mb-0.5">À</label>
                  <FormInput 
                    className={`w-full border-b border-black ${headerBg} uppercase h-4 box-border`} 
                    value={data.buyer.birthPlace || ''} 
                    onChange={v => updatePerson('buyer', 'birthPlace', v)} 
                    readOnly={readOnly} isPdfMode={isPdfMode}
                  />
                </div>
              </div>
            )}

            <div className="bg-gray-50/50 p-2 border border-gray-200 mb-1">
                <p className="font-bold text-[9px] mb-1">Je certifie (cocher la case correspondante) :</p>
                <div className="space-y-1">
                    <Checkbox label="Acquérir le véhicule désigné ci-dessus aux dates et heures indiquées par l'ancien propriétaire" checked={data.agreedToConditions} onClick={() => onChange && onChange({...data, agreedToConditions: !data.agreedToConditions})} className="items-start" />
                    <Checkbox label="Avoir été informé de la situation administrative du véhicule." checked={data.agreedToConditions} onClick={() => onChange && onChange({...data, agreedToConditions: !data.agreedToConditions})} className="items-start" />
                </div>
            </div>
         </div>

         <div className="flex justify-between items-end pr-2 mb-0">
             <div className="flex items-end gap-1 mb-1 w-1/2">
                <span className="font-bold text-[10px] whitespace-nowrap">Fait à</span>
                <FormInput 
                  className="border-b border-black bg-transparent px-1 font-bold h-4 box-border flex-1 uppercase" 
                  value={data.seller.city} 
                  onChange={v => updatePerson('seller', 'city', v)} 
                  readOnly={readOnly} isPdfMode={isPdfMode}
                />
                <span className="font-bold text-[10px] whitespace-nowrap ml-1">, le</span>
                <FormInput 
                  type="date"
                  className="border-b border-black bg-transparent px-1 font-bold h-4 box-border flex-1" 
                  value={data.saleDate} 
                  onChange={v => onChange && onChange({...data, saleDate: v})} 
                  readOnly={readOnly} isPdfMode={isPdfMode}
                />
             </div>
             
             <div className="text-center w-52">
                 <p className="font-bold text-[9px] mb-0.5">Signature du nouveau propriétaire,</p>
                 <p className="italic text-[7px] mb-1 leading-tight text-gray-600">(Pour les sociétés : nom et qualité<br/>du signataire et cachet)</p>
                 <div className="h-10 border border-gray-300 bg-white"></div>
             </div>
         </div>
      </div>
      
      <div className="absolute bottom-2 right-4 text-[7px] text-gray-400 flex items-center">
        <span>Je m’oppose à la réutilisation de mes données personnelles à des fins de prospection commerciale</span>
        <div className="w-3 h-3 border border-gray-400 ml-1"></div>
      </div>
    </div>
  );
};

const CerfaForm: React.FC<CerfaFormProps> = ({ data, onChange, isPdfMode = false }) => {
  return (
    <div className={`flex flex-col items-center ${isPdfMode ? 'bg-white' : 'bg-gray-500/50'}`}>
      <div id={isPdfMode ? "cerfa-pdf-source" : "cerfa-print-area"}>
          {/* Exemplaire 1 */}
          <div className="mb-0">
            <SingleCerfaPage data={data} onChange={onChange} copyNumber={1} isPdfMode={isPdfMode} />
          </div>
          {/* Interactive mode shows copy 2, PDF mode logic handles copy 2 duplication in App.tsx */}
          {!isPdfMode && (
            <div className="mt-8">
                <SingleCerfaPage data={data} onChange={onChange} copyNumber={2} isPdfMode={isPdfMode} />
            </div>
          )}
      </div>
    </div>
  );
};

export default CerfaForm;
